import type { StoreExpenses } from "@components/modules/expenses/listing/StoreExpenses";
import {
  ExpenseExportDocument,
  type ExpenseExportQuery,
  type ExpenseExportQueryVariables,
  ExpenseXmlExportDocument,
  type ExpenseXmlExportQuery,
  type ExpenseXmlExportQueryVariables,
  ExportExpensesSummaryDocument,
  type ExportExpensesSummaryQuery,
  type ExportExpensesSummaryQueryVariables,
  GenerateExpensePaymentOrderDocument,
  type GenerateExpensePaymentOrderQuery,
  type GenerateExpensePaymentOrderQueryVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { isEmpty } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

export class ExpenseListNavbarStore {
  private appStore: AppStore;
  private expensesStore: StoreExpenses;

  qrCodesModal = new DisclosureState();
  qrCodesModalConfirm = new DisclosureState();
  paymentOrderModal = new DisclosureState();

  @observable.ref
  warn = false;
  @observable.ref
  duplicatePayment = false;

  private readonly expensesExportFetcher = new FetchHelper<
    ExpenseExportQuery,
    ExpenseExportQueryVariables
  >(ExpenseExportDocument);

  private readonly expensesSummaryExportFetcher = new FetchHelper<
    ExportExpensesSummaryQuery,
    ExportExpensesSummaryQueryVariables
  >(ExportExpensesSummaryDocument);

  private readonly paymentOrderExportFetcher = new FetchHelper<
    GenerateExpensePaymentOrderQuery,
    GenerateExpensePaymentOrderQueryVariables
  >(GenerateExpensePaymentOrderDocument);

  private readonly expenseXmlExportFetcher = new FetchHelper<
    ExpenseXmlExportQuery,
    ExpenseXmlExportQueryVariables
  >(ExpenseXmlExportDocument);

  constructor(appStore: AppStore, expensesStore: StoreExpenses) {
    makeObservable(this);
    this.appStore = appStore;
    this.expensesStore = expensesStore;
  }

  @computed
  get showExpensesExport(): boolean {
    return !!this.appStore.workspaceStore.settings?.exportSettings.expenses;
  }

  @computed
  get showExpensesSummaryExport(): boolean {
    return !this.appStore.workspaceStore.settings?.hide_expense_summary;
  }

  @computed
  get showPremierExport(): boolean {
    return !!this.appStore.workspaceStore.settings?.exportSettings.premier;
  }

  @computed
  get showOmegaExport(): boolean {
    return !!this.appStore.workspaceStore.settings?.exportSettings.omega;
  }

  @computed
  get showPohodaExport(): boolean {
    return !!this.appStore.workspaceStore.settings?.exportSettings.pohoda;
  }

  @computed
  get showVarioExport(): boolean {
    return !!this.appStore.workspaceStore.settings?.exportSettings.vario;
  }

  @computed
  get showTransakcioExport(): boolean {
    return !!this.appStore.workspaceStore.settings?.exportSettings.transakcio;
  }

  @computed
  get hasSelectedExpenses(): boolean {
    return !isEmpty(this.expensesStore.selectedExpensesIds);
  }

  @computed
  get isExporting(): boolean {
    return (
      this.expensesExportFetcher.isLoading.value ||
      this.expensesSummaryExportFetcher.isLoading.value ||
      this.paymentOrderExportFetcher.isLoading.value ||
      this.expenseXmlExportFetcher.isLoading.value ||
      this.expensesStore.varioExportFetcher.isLoading.value ||
      this.expensesStore.transakcioExportFetcher.isLoading.value ||
      this.expensesStore.isOmegaExporting ||
      this.expensesStore.isAbraFlexiExporting ||
      this.expensesStore.isPremierExporting
    );
  }

  @computed
  get isPaymentOrderExportLoading(): boolean {
    return this.paymentOrderExportFetcher.isLoading.value;
  }

  @action.bound
  showDuplicatePayment(): void {
    this.duplicatePayment = true;
  }
  @action.bound
  hideDuplicatePayment(): void {
    this.duplicatePayment = false;
  }
  @action.bound
  showWarning(): void {
    this.warn = true;
  }
  @action.bound
  hideWarning(): void {
    this.warn = false;
  }

  @action.bound
  checkSelectedExpenses(callback: () => void): void {
    if (!this.hasSelectedExpenses) {
      this.warn || this.showWarning();
    } else {
      this.warn && this.hideWarning();
      callback();
    }
  }
  @action.bound
  onAction(): void {
    const unprocessableIds = this.expensesStore.selectedExpensesIds.filter(
      (id) => this.expensesStore.unprocessableExpenseIds.includes(id),
    );
    this.expensesStore.clearExpenseIds(unprocessableIds);
    this.hideDuplicatePayment();
    if (this.expensesStore.selectedExpensesIds.length > 0) {
      this.checkSelectedExpenses(() => this.paymentOrderModal.onOpen());
    }
  }

  @action.bound
  async validateQrCodesModal(): Promise<void> {
    const res = await this.expensesStore.modalForms.qrCodes.validate();

    if (res.hasError) return;

    this.qrCodesModalConfirm.onOpen();
  }

  @action.bound
  async validateAndExportPaymentOrderModal(
    variables: GenerateExpensePaymentOrderQueryVariables,
  ): Promise<void> {
    const res = await this.expensesStore.modalForms.paymentOrder.validate();

    if (res.hasError) return;

    await this.exportPaymentOrder(variables);
  }

  @action.bound
  async exportExpenses(): Promise<void> {
    const [data] = await this.expensesExportFetcher.fetch({
      filters: this.expensesStore.queryParams.filters,
    });

    if (!data?.expenseExport) return;

    window.location.href = data.expenseExport;
  }

  @action.bound
  async exportExpensesSummary(): Promise<void> {
    if (!this.hasSelectedExpenses) {
      return;
    }

    const [data] = await this.expensesSummaryExportFetcher.fetch({
      ids: this.expensesStore.selectedExpensesIds,
    });

    if (!data?.expenseSummaryExportBulk) return;

    window.location.href = data.expenseSummaryExportBulk;
  }

  @action.bound
  async exportExpensesXml(): Promise<void> {
    const [data] = await this.expenseXmlExportFetcher.fetch({
      filters: this.expensesStore.queryParams.filters,
    });

    if (!data?.expenseXMLExport) return;

    window.location.href = data.expenseXMLExport;
  }

  @action.bound
  async exportPaymentOrder(
    variables: GenerateExpensePaymentOrderQueryVariables,
  ): Promise<void> {
    if (!this.hasSelectedExpenses) {
      return;
    }

    const [data] = await this.paymentOrderExportFetcher.fetch(variables);

    if (!data?.generateExpensePaymentOrder) return;

    this.paymentOrderModal.onClose();
    this.qrCodesModal.onClose();

    if (data.generateExpensePaymentOrder.url != null) {
      window.location.href = data.generateExpensePaymentOrder.url;
      this.expensesStore.clearExpenseIds([]);
      this.expensesStore.refetch?.();
    } else if (data.generateExpensePaymentOrder.unprocessable_expense_ids) {
      this.expensesStore.unprocessableExpenseIds =
        data.generateExpensePaymentOrder.unprocessable_expense_ids;
      this.showDuplicatePayment();
    }
  }

  @action.bound
  async exportPremier(): Promise<void> {
    return this.expensesStore.exportPremier();
  }

  @action.bound
  async exportOmega(): Promise<void> {
    return this.expensesStore.exportOmega();
  }

  @action.bound
  async exportAbraFlexi(): Promise<void> {
    return this.expensesStore.exportAbraFlexi();
  }

  @action.bound
  async exportPohoda(): Promise<void> {
    return this.expensesStore.exportToPohoda(
      this.expensesStore.selectedExpensesIds,
    );
  }

  @action.bound
  async exportVario(): Promise<void> {
    return this.expensesStore.exportToVario(
      this.expensesStore.selectedExpensesIds,
    );
  }

  @action.bound
  async exportTransakcio(): Promise<void> {
    return this.expensesStore.exportToTransakcio(
      this.expensesStore.selectedExpensesIds,
    );
  }
}
